'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';

import { IExperienceDictionary } from '@/dictionaries/pages/experience/types';
import useExperienceContext from '@/hooks/useExperienceContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import useUserContext from '@/hooks/useUserContext';
import useCurrency from '@/hooks/useCurrency';
import useVillabooking from '@/hooks/useVillabookingContext';
import { Experience } from '@/services/experienceTypes';
import { experienceNeedLogin } from '@/utils/experienceNeedLogin';
import { toggleExperienceShortlist } from '@/utils';

import { ShortList } from '../CategorySections/ExperienceType';
import { IoIosArrowBack } from 'react-icons/io';
import EmptyStarIcon from '../Icons/EmptyStar';
import FilledStarIcon from '../Icons/FilledStar';
import Button from '../Button';

import styles from './styles.module.css';

type Props = {
  dict: IExperienceDictionary;
  showReviews: boolean;
  showMap: boolean;
  showOptions: boolean;
  experience: Experience;
  lang: string;
};

export default function ExperienceHeader({
  dict,
  showReviews,
  showMap,
  showOptions,
  experience,
  lang,
}: Props) {
  const [visibleSection, setVisibleSection] = useState('');
  const [fixHeader, setFixHeader] = useState(false);
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );

  const { inShortList, handleShortList } = useExperienceContext();
  const { currency } = useCurrency();
  const { isLogged } = useUserContext();
  const { isAvailable } = useVillabooking();

  const needLogin = experienceNeedLogin(isLogged, experience);

  const showConditions = !experience.inVilla || isLogged;

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);

    if (element) {
      const topOffset = element.offsetTop - 80;
      window.scrollTo({ top: topOffset, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'media', label: 'media' },
        { id: 'the-experience', label: 'experience' },
        { id: 'options', label: 'options' },
        { id: 'conditions', label: 'conditions' },
        { id: 'map', label: 'map' },
        { id: 'reviews', label: 'reviews' },
        { id: 'enquiry', label: 'enquiry' },
      ];

      for (let section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();

          if (rect.top < 81 && rect.top > -130) {
            setVisibleSection(section.label);
            break;
          } else {
            setVisibleSection('');
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isFavourite = favourites.some((item) => item.id === experience.id);

    if (isFavourite) {
      handleShortList(true);
    } else {
      handleShortList(false);
    }
    // eslint-disable-next-line
  }, [favourites]);

  useEffect(() => {
    const handleScroll = () => {
      const mediaElement = document.querySelector('#media');

      if (!mediaElement) return;

      const mediaRect = mediaElement.getBoundingClientRect();

      if (mediaRect.top <= 0) {
        setFixHeader(true);
      } else {
        setFixHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixHeader]);

  useEffect(() => {
    const hash = window.location.hash.substring(1);

    if (hash) {
      scrollToElement(hash);
    }
  }, []);

  return (
    <div
      className={`flex items-center justify-between w-full h-20 fixed left-0 px-[15px] tablet:px-10 bg-white shadow-default z-[998] -top-[80px] ${
        fixHeader ? styles['header-down'] : styles['header-up']
      }`}>
      <div className="flex gap-5">
        <Link aria-label="Go to experiences page" href={'./'}>
          <div className="flex items-center xl:max-w-[300px] desktop:max-w-[140px]">
            <IoIosArrowBack className="h-6 w-6 text-gray-500 mr-1" />
            <span className="hidden desktop:block text-gray-700 text-center">
              {dict.header.back}
            </span>
          </div>
        </Link>
        <nav className="hidden desktop:flex flex-col items-center gap-5 text-base relative desktop:flex-row desktop:gap-0">
          <div
            className={`${styles.underline} ${
              visibleSection === 'experience' ? styles['underline-active'] : ''
            }`}
            onClick={() => scrollToElement('the-experience')}>
            <span className="z-10">{dict.header.experience}</span>
          </div>
          {showOptions && (
            <div
              className={`${styles.underline} ${
                visibleSection === 'options' ? styles['underline-active'] : ''
              }`}
              onClick={() => scrollToElement('options')}>
              <span className="z-10">{dict.header.options}</span>
            </div>
          )}
          {showConditions && (
            <div
              className={`${styles.underline} ${
                visibleSection === 'conditions'
                  ? styles['underline-active']
                  : ''
              }`}
              onClick={() => scrollToElement('conditions')}>
              <span className="z-10">{dict.header.conditions}</span>
            </div>
          )}
          {showMap && (
            <div
              className={`${styles.underline} ${
                visibleSection === 'map' ? styles['underline-active'] : ''
              }`}
              onClick={() => scrollToElement('map')}>
              <span className="z-10">{dict.header.map}</span>
            </div>
          )}
          {showReviews && (
            <div
              className={`${styles.underline} ${
                visibleSection === 'reviews' ? styles['underline-active'] : ''
              }`}
              onClick={() => scrollToElement('reviews')}>
              <span className="z-10">{dict.header.reviews}</span>
            </div>
          )}
        </nav>
      </div>
      <div className="flex items-center gap-4">
        <div
          className="flex text-primary gap-2 items-center justify-center xl:max-w-[300px] desktop:max-w-[145px] cursor-pointer"
          onClick={() =>
            toggleExperienceShortlist(
              experience,
              lang,
              favourites,
              setFavourites,
              inShortList,
              currency,
            )
          }>
          {inShortList ? (
            <>
              <FilledStarIcon className="fill-current text-inherit w-6 h-6" />
              <p className="hidden mobileL:block text-center">
                {dict.request.removeShortlist}
              </p>
            </>
          ) : (
            <>
              <EmptyStarIcon className="fill-current text-inherit w-6 h-6" />
              <p className="hidden mobileL:block text-center">
                {dict.request.addShortlist}
              </p>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant={'outline'}
            className="h-[50px] px-4 w-auto hover:bg-white hover:text-primary"
            onClick={() => scrollToElement('enquiry')}>
            {dict.header.enquiry}
          </Button>
          {!needLogin ? (
            experience.inVilla && !isAvailable ? (
              <></>
            ) : (
              <Button
                className="hidden desktop:block h-[50px] py-0 px-4 w-auto xl:max-w-[500px] desktop:max-w-[230px] hover:text-white hover:bg-primary"
                onClick={() => scrollToElement('request')}>
                {dict.header.request}
              </Button>
            )
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
}
